import classnames from 'classnames';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import omit from 'omit.js';
import {Warning, getNid, cssUtil} from "../../wui-core/src";
import {addEventListener, prefix} from "../../wui-core/src/index";
import {AffixProps, AffixState} from './iAffix'

const {isShouldUpdate} = Warning;

function noop() {}
function defaultTarget() {
    return window
}
const Affix: React.FC<AffixProps> = (incomingProps) => {
    // 使用对象解构设置默认值,避免使用已废弃的 defaultProps
    const props = {
        horizontal: false,
        zIndex: 2019,
        canHidden: false,
        childrenRef: undefined,
        initCalc: false,
        ...incomingProps,
        // 如果传入的值是 undefined 或 null,使用默认值
        target: incomingProps.target || defaultTarget,
        onChange: incomingProps.onChange || noop,
        onTargetChange: incomingProps.onTargetChange || noop,
    };

    // 解构props,避免在依赖数组中使用props对象
    const {
        horizontal,
        target,
        onChange,
        onTargetChange,
        zIndex,
        canHidden,
        childrenRef,
        initCalc,
        offsetTop,
        offsetBottom,
        container,
        getPopupContainer
    } = props;
    // ==================== State ====================
    const [state, setState] = useState<AffixState>({
        affixed: false,
        initTop: 0,
        initBottom: 0,
        initLeft: 0,
        top: 0,
        left: 0,
        bottom: 0,
        marginTop: 0,
        marginBottom: 0,
        marginLeft: 0,
        height: 0,
        width: 0,
        containerHeight: 0,
        containerWidth: 0,
        containerId: `${prefix}-affix-container` + Math.random().toString(26).substring(2, 10),
        isMount: false
    });

    // ==================== Refs ====================
    // offsetTop的计算值
    const offsetTopRef = useRef<number | undefined>(
        props.hasOwnProperty('offsetBottom')
            ? undefined
            : (props.hasOwnProperty('offsetTop') ? props.offsetTop : 0)
    );

    // 标记是否已经调整过 offsetTopRef (避免重复累加)
    const hasAdjustedOffsetTopRef = useRef(false);

    const observerWidthRef = useRef<number>(-1);
    const resizeHandlerRef = useRef<any>();
    const scrollHandlerRef = useRef<{remove:()=> void}>();
    const boxDomRef = useRef<HTMLDivElement>(null);
    const latestEventRef = useRef<MouseEvent | undefined>();

    // ==================== Methods ====================
    /**
     * 获取container
     */
    const getContainerDOM = useCallback((nextProps?: AffixProps): HTMLElement | Element => {
        const containerEl = nextProps
            ? (nextProps.getPopupContainer || nextProps.container)
            : (getPopupContainer || container);

        if (!containerEl) {
            return document.body;
        }
        if (containerEl != document.body) {
            return containerEl;
        }
        return containerEl;
    }, [getPopupContainer, container]);

    /**
     * 第一次 和 每次srcoll和resize也会执行该方法
     * initTop,initLeft，marginTop,marginLeft都是不变的
     */
    const getInitPosition = useCallback((nextProps?: AffixProps, flag?: boolean) => {
        const container = getContainerDOM(nextProps) as HTMLElement;
        // children是个变化，所以在nextprops要传入childrenRef，否则直接使用后面的语句
        const thisElm = nextProps && nextProps.childrenRef ? nextProps.childrenRef : boxDomRef.current;

        if (!thisElm) return;

        const thisEleCon = boxDomRef.current; // children是个变化的情况，top等值需要用this的值

        if (!thisEleCon) return;

        // 使用 requestAnimationFrame 批量读取布局信息，避免布局抖动
        requestAnimationFrame(() => {
            const containerRect = container.getBoundingClientRect();
            const thisElemRect = thisEleCon.getBoundingClientRect();

            const height = thisElm.offsetHeight;
            const width = thisElm.offsetWidth;
            const containerHeight = container.offsetHeight;
            const containerWidth = container.offsetWidth;

            const {top, left, bottom} = thisElemRect;
            const marginTop = top - containerRect.top;
            const marginLeft = left - containerRect.left;
            const marginBottom = containerRect.bottom - bottom;

            setState(prev => ({
                ...prev,
                height,
                width,
                containerHeight,
                containerWidth,
                top: top,
                left: left,
                initTop: flag ? top : prev.initTop,
                initBottom: flag ? bottom : prev.initBottom,
                initLeft: flag ? left : prev.initLeft,
                marginTop: marginTop,
                marginBottom: marginBottom,
                marginLeft: marginLeft
            }));
        });
    }, [getContainerDOM]);

    // 使用 ref 存储 props 中的回调函数,避免依赖 props 对象
    const onChangeRef = useRef(onChange);
    const onTargetChangeRef = useRef(onTargetChange);
    const offsetBottomRef = useRef(offsetBottom);

    useEffect(() => {
        onChangeRef.current = onChange;
        onTargetChangeRef.current = onTargetChange;
        offsetBottomRef.current = offsetBottom;
    }, []);

    // 标记是否需要调用getInitPosition
    const needRecalcRef = useRef(false);

    /**
     * 主要用于处理scroll以及resize事件重新计算布局
     */
    const handleTargetChange = useCallback((evt: MouseEvent) => {
        latestEventRef.current = evt;
        const container = getContainerDOM() as HTMLElement;
        const {top, left, bottom} = container.getBoundingClientRect();

        setState(prev => {
            const newState = {
                ...prev,
                top: top + prev.marginTop,
                left: left + prev.marginLeft,
                bottom: window.innerHeight - bottom + prev.marginBottom,
                containerHeight: container.offsetHeight,
                containerWidth: container.offsetWidth,
            };

            // 判断 affixed 状态 (原来在 setState 回调中的逻辑)
            const shouldAffixByTop = offsetTopRef.current !== undefined && newState.top < offsetTopRef.current;
            const shouldAffixByBottom = offsetBottomRef.current !== undefined && newState.bottom > offsetBottomRef.current;
            const shouldAffix = shouldAffixByTop || shouldAffixByBottom;

            if (shouldAffix && !prev.affixed) {
                onChangeRef.current?.({affixed: true, event: evt});
                newState.affixed = true;
            } else if (!shouldAffix && prev.affixed) {
                onChangeRef.current?.({affixed: false, event: evt});
                newState.affixed = false;
            }

            // 标记需要重新计算位置
            needRecalcRef.current = true;

            return newState;
        });
    }, [getContainerDOM]);

    // 在状态更新后调用 onTargetChange 和 getInitPosition
    // 注意: 只在 handleTargetChange 触发时才执行,避免无限循环
    useEffect(() => {
        if (needRecalcRef.current) {
            needRecalcRef.current = false;
            onTargetChangeRef.current?.(state);
            getInitPosition(undefined, false);
        }
    }, [state.top, state.left, state.bottom, state.containerHeight, state.containerWidth, getInitPosition]); // 只监听 handleTargetChange 修改的字段

    /**
     * ResizeObserver 回调
     */
    const handleResize = useCallback((entries: any) => {
        entries.forEach((item: any) => {
            if (observerWidthRef.current === -1) {
                observerWidthRef.current = item.contentRect.width;
            }
            if (observerWidthRef.current !== item.contentRect.width) {
                observerWidthRef.current = item.contentRect.width;
                if (observerWidthRef.current !== 0) {
                    handleTargetChange(item as any);
                }
            }
        });
    }, [handleTargetChange]);

    // ==================== Effects ====================
    /**
     * componentDidMount + componentWillUnmount
     */
    useEffect(() => {
        const listenTarget = target?.();

        // 只有当 listenTarget 存在且不是 window 时才调整 offsetTopRef
        if (listenTarget && listenTarget !== window && !hasAdjustedOffsetTopRef.current) {
            const {top} = (listenTarget as HTMLElement).getBoundingClientRect();
            offsetTopRef.current = (offsetTopRef.current as number) + top;
            hasAdjustedOffsetTopRef.current = true; // 标记已调整,避免重复累加
        }

        if (listenTarget) {
            if (listenTarget !== window) {
                resizeHandlerRef.current = new ResizeObserver(handleResize);
                resizeHandlerRef.current.observe(listenTarget);
            } else {
                resizeHandlerRef.current = addEventListener(listenTarget, 'resize', handleTargetChange);
            }
            scrollHandlerRef.current = addEventListener(listenTarget, 'scroll', handleTargetChange);
        }

        setState(prev => ({
            ...prev,
            isMount: true
        }));

        getInitPosition(undefined, true);

        // cleanup (componentWillUnmount)
        return () => {
            const cleanupTarget = target?.();
            if (cleanupTarget) {
                scrollHandlerRef.current?.remove();

                if (cleanupTarget !== window) {
                    resizeHandlerRef.current?.disconnect();
                } else {
                    resizeHandlerRef.current?.remove();
                }
            }
        };
    }, [target, handleResize, handleTargetChange, getInitPosition]); // 添加依赖项

    /**
     * componentDidUpdate: 监听 getPopupContainer 变化
     */
    const prevGetPopupContainerRef = useRef(getPopupContainer);
    useEffect(() => {
        if (prevGetPopupContainerRef.current !== getPopupContainer) {
            // 创建临时 props 对象传递给 getInitPosition
            const tempProps: AffixProps = {
                getPopupContainer,
                container,
                childrenRef
            };
            getInitPosition(tempProps, true);
            prevGetPopupContainerRef.current = getPopupContainer;
        }
    }, [getPopupContainer, container, childrenRef, getInitPosition]);

    // ==================== Computed ====================
    /**
     * 计算 fixed 样式
     */
    const {fixStyle, boxStyle} = useMemo(() => {
        let fixStyle: React.CSSProperties = {};
        let boxStyle: React.CSSProperties = {};

        // 组件挂载完成才计算高度，否则高度获取有偏差
        if (!state.isMount) {
            return {fixStyle, boxStyle};
        }

        // 组件initCalc初始化计算的时候应添加初始化高度，用户只传getPopupContainer，不传target时，以getPopupContainer坐标为基础高度(都传时以target坐标为基础)
        if (target?.() === window && initCalc && getContainerDOM() !== document.body) {
            offsetTopRef.current = offsetTop! + state.initTop;
        }

        // 修改，添加(state.top - state.marginTop == 0)的判断，谨防height+offsetTop >= containerHeight
        if (!initCalc && (!state.affixed || (offsetTopRef.current !== undefined && state.top - state.marginTop == 0))) {
            return {fixStyle, boxStyle};
        }

        const h = (state.top - state.marginTop + state.containerHeight) - state.height;

        // modal包含affix时，因modal wui-modal-content层存在transform属性导致affix组件内的fixed定位失效变成了相对modal的定位
        const isParentModalNode = cssUtil.parent(
            document.getElementById(state.containerId)!,
            ".wui-modal-content [tinper-next-role=container]"
        );
        const modalLeft = getPopupContainer?.getBoundingClientRect()?.left || state.left;

        if (offsetTopRef.current !== undefined && state.top < offsetTopRef.current) {
            const finalTop = isParentModalNode
                ? offsetTop
                : canHidden
                    ? (h < 0 ? h : Math.min(h, offsetTopRef.current))
                    : offsetTopRef.current;

            fixStyle = {
                position: "fixed",
                top: finalTop,
                left: isParentModalNode
                    ? modalLeft - state.left
                    : horizontal
                        ? state.initLeft
                        : state.left,
                height: state.height,
                width: state.width,
                zIndex: zIndex,
            };
            boxStyle = {height: state.height};
        } else if (offsetBottom !== undefined && state.bottom > offsetBottom) {
            fixStyle = {
                position: "fixed",
                bottom: isParentModalNode
                    ? offsetBottom
                    : canHidden
                        ? (h < 0 ? h : Math.min(h, offsetBottom))
                        : offsetBottom,
                left: isParentModalNode
                    ? modalLeft - state.left
                    : horizontal
                        ? state.initLeft
                        : state.left,
                height: state.height,
                width: state.width,
                zIndex: zIndex,
            };
            boxStyle = {height: state.height};
        }

        return {fixStyle, boxStyle};
    }, [
        state.isMount,
        state.affixed,
        state.top,
        state.bottom,
        state.marginTop,
        state.containerHeight,
        state.height,
        state.left,
        state.initLeft,
        state.width,
        state.containerId,
        state.initTop,
        target,
        initCalc,
        getPopupContainer,
        offsetTop,
        offsetBottom,
        canHidden,
        horizontal,
        zIndex,
        getContainerDOM
    ]);

    // ==================== Render ====================
    isShouldUpdate("Affix", props);
    const adapterNid = getNid(props);

    return (
        <div
            {...omit(props, [
                "offsetTop",
                "offsetBottom",
                "horizontal",
                "container",
                "getPopupContainer",
                "target",
                "onChange",
                "onTargetChange",
                "zIndex",
                "canHidden",
                "childrenRef",
                "initCalc"
            ])}
            className={classnames(`${prefix}-affix-container`, props.className)}
            id={state.containerId}
            style={boxStyle}
            {...adapterNid}
            ref={boxDomRef}
        >
            <div className={`${prefix}-affix-content`} style={fixStyle}>
                {props.children}
            </div>
        </div>
    );
};

export default Affix;
